import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { X } from 'lucide-react';

interface TermsDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  termsContent: string;
  version: string;
}

const TermsDialog: React.FC<TermsDialogProps> = ({
  open,
  onClose,
  onAccept,
  termsContent,
  version
}) => {
  const isMobile = useIsMobile();
  const mdComponents = {
    h1: (p: any) => <h1 className="text-3xl font-bold mt-6 mb-3" {...p} />,
    h2: (p: any) => <h2 className="text-2xl font-semibold mt-5 mb-2" {...p} />,
    h3: (p: any) => <h3 className="text-xl font-medium mt-4 mb-2" {...p} />,
    h4: (p: any) => <h4 className="text-lg font-medium mt-4 mb-2" {...p} />,
    h5: (p: any) => <h5 className="text-base font-medium mt-3 mb-2" {...p} />,
    h6: (p: any) => <h6 className="text-sm font-semibold mt-3 mb-2 uppercase tracking-wide" {...p} />,
    p: (p: any) => <p className="my-3 leading-7" {...p} />,
    a: (p: any) => <a className="text-blue-600 hover:underline" {...p} />,
    ul: (p: any) => <ul className="list-disc pl-6 my-4" {...p} />,
    ol: (p: any) => <ol className="list-decimal pl-6 my-4" {...p} />,
    li: (p: any) => <li className="my-1" {...p} />,
    blockquote: (p: any) => <blockquote className="border-l-4 pl-4 italic my-4 text-muted-foreground" {...p} />,
    hr: (p: any) => <hr className="my-6 border-muted" {...p} />,
    table: (p: any) => <table className="table-auto w-full my-4 border-collapse" {...p} />,
    thead: (p: any) => <thead className="bg-muted/20" {...p} />,
    tbody: (p: any) => <tbody {...p} />,
    tr: (p: any) => <tr className="border-b" {...p} />,
    th: (p: any) => <th className="text-left font-semibold p-2" {...p} />,
    td: (p: any) => <td className="p-2 align-top" {...p} />,
    img: (p: any) => <img className="max-w-full rounded" {...p} />,
    strong: (p: any) => <strong className="font-semibold" {...p} />,
    em: (p: any) => <em className="italic" {...p} />,
    del: (p: any) => <del className="opacity-70" {...p} />,
    code: ({ inline, className, children, ...rest }: any) => {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 rounded bg-muted/30 text-sm" {...rest}>
            {children}
          </code>
        );
      }
      return (
        <pre className="my-4 overflow-x-auto rounded bg-muted/20 p-4">
          <code className={className}>{children}</code>
        </pre>
      );
    },
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onClose}>
        <DrawerContent className="w-full max-w-full p-0 bg-card">
          <DrawerHeader>
            <div className="flex justify-between items-start p-6 pb-4 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
              <DrawerTitle className="text-2xl font-bold text-foreground">
                AI Harold Beta Terms of Use
              </DrawerTitle>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-2 hover:bg-white/5"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </DrawerHeader>
          <div className="p-6 overflow-y-auto" style={{ maxHeight: '60vh' }}>
            <div className="prose dark:prose-invert max-w-none bg-transparent p-4 rounded border">
              <ReactMarkdown remarkPlugins={[[remarkGfm, { breaks: true }]]} components={mdComponents}>
                {termsContent}
              </ReactMarkdown>
              <div className="text-xs text-gray-500 mt-4">
                Version: {version}
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-white/10 bg-gradient-to-t from-primary/5 to-transparent">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 backdrop-blur-sm border-white/20 hover:bg-white/10"
              >
                Decline
              </Button>
              <Button
                onClick={onAccept}
                className="flex-1 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
              >
                I Accept the Terms
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-screen-lg sm:max-w-screen-lg max-h-[90vh] bg-transparent shadow-none border-0 flex flex-col">

        <div className="flex-1 min-h-0 overflow-y-auto bg-card/90 rounded-xl">
          <div className="flex justify-between items-start p-6 pb-4 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
            <DialogTitle className="text-2xl font-bold text-foreground">
              AI Harold Beta Terms of Use
            </DialogTitle>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-2 hover:bg-white/5"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="prose dark:prose-invert max-w-none bg-transparent p-4 rounded border">
            <ReactMarkdown remarkPlugins={[[remarkGfm, { breaks: true }]]} components={mdComponents}>
              {termsContent}
            </ReactMarkdown>
            <div className="text-xs text-gray-500 mt-4">
              Version: {version}
            </div>
          </div>
          <div className="border-t border-white/10 p-6 bg-gradient-to-t from-primary/5 to-transparent">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 backdrop-blur-sm border-white/20 hover:bg-white/10"
              >
                Decline
              </Button>
              <Button
                onClick={onAccept}
                className="flex-1 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
              >
                I Accept the Terms
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TermsDialog;
